import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClickAwayListener,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popper,
  TextField,
  Typography,
  withWidth,
  isWidthDown,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Fuse from "fuse.js";
import axios from "axios";
import { useQuery } from "react-query";
import useDebounce from "../../../../hooks/useDebounce";

const SearchResults = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  searchResults,
  width,
}) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 3 }}
      transition
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          style={{
            width: isWidthDown("xs", width) ? "200px" : "500px",
            height: isWidthDown("xs", width) ? 310 : 470,
            overflowY: "auto",
          }}
        >
          <List dense component="nav" aria-label="main mailbox folders">
            {searchResults?.slice(0, 49)?.map((result, i) => (
              <React.Fragment key={i}>
                <ListItem
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                  button
                  onClick={() => onSelect(result?.item)}
                >
                  <Typography variant="subtitle1">
                    {result?.item?.location_name}
                  </Typography>
                  <Grid container>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Station ID</Typography>
                      <Typography variant="body1">
                        {result?.item?.location_id || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Station Abbr.</Typography>
                      <Typography variant="body1">
                        {result?.item?.location_display_label || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Location Type</Typography>
                      <Typography variant="body1">
                        {result?.item?.locationtype || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Water Body</Typography>
                      <Typography variant="body1">
                        {result?.item?.waterbody_name || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Organizations</Typography>
                      <Typography variant="body1">
                        {result?.item?.organizations.join(", ") || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={isWidthDown("xs", width) ? 12 : 6}>
                      <Typography variant="caption">Sources</Typography>
                      <Typography variant="body1">
                        {result?.item?.sources.join(", ") || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const Search = ({ onSelect, width }) => {
  const searchRef = useRef(null);
  const { data: options } = useQuery(
    ["Search Options"],
    async () => {
      try {
        return await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/wells`
        );
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const [value, setValue] = useState("");
  const debouncedSearchValue = useDebounce(value, 200);
  const [searchResults, setSearchResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!searchResults?.length);
  }, [searchResults]);

  const fuzzySearcher = useMemo(() => {
    if (options?.data) {
      return new Fuse(options?.data, {
        ignoreLocation: true,
        keys: [
          "location_name",
          "location_id",
          "location_display_label",
          "waterbody_name",
          "locationtype",
          "organizations",
          "sources",
        ],
      });
    }
  }, [options]);

  const handleClose = (event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleChange = (event) => {
    setValue(event?.target?.value);
  };

  useEffect(() => {
    const results = fuzzySearcher && fuzzySearcher.search(debouncedSearchValue);
    setSearchResults(results);
  }, [debouncedSearchValue, fuzzySearcher]);

  return (
    <>
      <TextField
        id="outlined-basic"
        label="Riverside Locations"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={handleChange}
        onFocus={() => !!value && setOpen(true)}
        placeholder="Locations attributes"
        ref={searchRef}
        style={{ width: "100%", minWidth: "200px" }}
        type="search"
        value={value}
        variant="outlined"
        size="small"
      />
      <SearchResults
        anchorEl={searchRef?.current}
        onClose={handleClose}
        onSelect={onSelect}
        open={open}
        searchResults={searchResults}
        width={width}
      />
    </>
  );
};

export default withWidth()(Search);
